import { apiClient } from './client';
import type { ApiResponse } from '../types';

export interface TopicSummary {
  subject: string | null;
  topic: string | null;
  attempts: number;
  avg_score_pct: number | null;
  avg_time_sec: number | null;
  total_credits: number | null;
  misconception_tags: string[] | null;
}

export interface EngagementTrend {
  day: string;
  active_students: number;
  events: number;
  total_credits: number | null;
}

export interface CreditTrend {
  day: string;
  earned: number | null;
  spent: number | null;
}

export interface PlanetProgression {
  stage: number;
  count: number;
  avg_credits: number | null;
}

export interface AssessmentEventFeedItem {
  event_id: string;
  external_event_id: string;
  created_at: string;
  student_name: string | null;
  source_platform: string | null;
  subject: string | null;
  topic: string | null;
  question_id: string | null;
  question_type: string | null;
  marks_awarded: number | null;
  max_marks: number | null;
  difficulty: string | null;
  time_spent_seconds: number | null;
  credits_awarded: number;
  misconception_tags: string[];
  skill_tags: string[];
}

export interface FlaggedActivity {
  spikes: Array<{
    user_id: string;
    student_name: string | null;
    hour_window: string;
    total_credits: number | null;
    event_count: number;
  }>;
  repeated: Array<{
    user_id: string;
    student_name: string | null;
    question_id: string | null;
    topic: string | null;
    attempt_count: number;
    last_attempt: string;
  }>;
}

export const analyticsApi = {
  getTopicSummary: (universeId: string, days?: number) =>
    apiClient
      .get<ApiResponse<TopicSummary[]>>(`/analytics/topics`, { params: { universeId, days } })
      .then((r) => r.data),

  getEngagementTrends: (universeId: string, days?: number) =>
    apiClient
      .get<ApiResponse<EngagementTrend[]>>(`/analytics/engagement`, { params: { universeId, days } })
      .then((r) => r.data),

  getCreditTrends: (universeId: string, days?: number) =>
    apiClient
      .get<ApiResponse<CreditTrend[]>>(`/analytics/credits`, { params: { universeId, days } })
      .then((r) => r.data),

  getPlanetProgression: (universeId: string) =>
    apiClient
      .get<ApiResponse<PlanetProgression[]>>(`/analytics/progression`, { params: { universeId } })
      .then((r) => r.data),

  getAssessmentEvents: (universeId: string, options?: { limit?: number; offset?: number; days?: number }) =>
    apiClient
      .get<ApiResponse<AssessmentEventFeedItem[]>>(`/analytics/events`, { params: { universeId, ...options } })
      .then((r) => r.data),

  getFlaggedActivity: (universeId: string, days?: number) =>
    apiClient
      .get<ApiResponse<FlaggedActivity>>(`/analytics/flagged`, { params: { universeId, days } })
      .then((r) => r.data),

  exportEngagementCsv: (universeId: string, days?: number) =>
    apiClient.get(`/analytics/export/engagement`, {
      params: { universeId, days },
      responseType: 'blob',
    }),

  exportAssessmentsCsv: (universeId: string, days?: number) =>
    apiClient.get(`/analytics/export/assessments`, {
      params: { universeId, days },
      responseType: 'blob',
    }),
};
